'use client';

import { useCallback, useState } from 'react';
import type { Listing, ScheduledUnitsType } from '@repo/api';
import { IconCalendarDuotone, IconChevronRight, IconVideoDuotone } from '@repo/svgs';
import { formatDate, getUnitTourHighlight, AUTH_MESSAGES, type ChatTourType } from '@repo/utils';
import { cn } from '@/lib/cn';
import { useStore } from '@/providers/StoreProvider';
import { useWithAuth } from '@/hooks/useWithAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ChatDetail } from '@/components/chat/ChatDetail';
import { urgencyParam } from '@/lib/listings/urgency';

interface UnitTourHighlightCardProps {
  listing: Listing;
  /**
   * When provided (e.g. inside UnitActionButtons sidebar), delegate to caller.
   * Receives the tour type so the caller can pass it on to chat creation.
   */
  onClick?: (tourType: ChatTourType) => void;
  /** Extra className applied to the root button element. */
  containerStyle?: string;
}

function formatOpenHouseDatetime(datetime: string): string {
  try {
    return formatDate(datetime, 'ddd, MMM D [at] h:mm a');
  } catch {
    return datetime;
  }
}

/**
 * Tappable card advertising the tour the unit qualifies for — an open house or a
 * video tour. Both open the tour scheduling chat, the same flow as the
 * "Schedule a tour" button; only the icon and copy differ.
 */
export function UnitTourHighlightCard({
  listing,
  onClick,
  containerStyle,
}: UnitTourHighlightCardProps) {
  const [isChatOpen, setIsChatOpen] = useState(false);

  const scheduledUnits = useStore(s => s.chat.scheduledUnits);
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const setCurrentUserScheduledUnits = useStore(s => s.chat?.setCurrentUserScheduledUnits);
  const withAuth = useWithAuth();
  const analytics = useAnalytics();

  const highlight = getUnitTourHighlight(listing);
  const addressForAnalytics = listing.fullAddress?.split(',')?.[0] ?? listing.addressSlug;

  const hasUserScheduledTour = scheduledUnits.some(
    (u: ScheduledUnitsType) => u.unit.slug === listing.slug,
  );

  const handleClose = useCallback(() => {
    setIsChatOpen(false);
    void setCurrentUserScheduledUnits?.();
  }, [setCurrentUserScheduledUnits]);

  const handleClick = useCallback(() => {
    if (!highlight) return;

    const unit = typeof window !== 'undefined' ? window.location.href : listing.slug;
    if (highlight === 'video_tour') {
      void analytics.track('video_tour_tap', { unit });
    } else {
      void analytics.track('open_house', { unit });
    }

    // If a parent-level handler is provided, delegate to it (sidebar case).
    if (onClick) {
      onClick(highlight);
      return;
    }

    const chat_source = listing.activeListing?.isFutureListing
      ? 'future'
      : listing.isVerified
        ? 'current_verified'
        : 'current_non_verified';

    if (!isLoggedIn) {
      void analytics.track('schedule_tour_non_login', {
        unitId: listing.slug,
        address: addressForAnalytics,
        screen: 'unit',
        chat_source,
        ...urgencyParam(listing),
      });
    }

    void withAuth(
      async () => {
        void analytics.track('schedule_tour_login', {
          unitId: listing.slug,
          address: addressForAnalytics,
          screen: 'unit',
          chat_type: hasUserScheduledTour ? 'continue' : 'new',
          chat_source,
          ...urgencyParam(listing),
        });
        setIsChatOpen(true);
      },
      // A video tour is "view", not "schedule" — the prompt says so. Open house
      // is still a scheduling flow, so it keeps the tour copy.
      highlight === 'video_tour' ? AUTH_MESSAGES.VIDEO_TOUR : AUTH_MESSAGES.CHAT_MESSAGE,
    );
  }, [
    highlight,
    onClick,
    addressForAnalytics,
    analytics,
    hasUserScheduledTour,
    isLoggedIn,
    listing,
    withAuth,
  ]);

  if (!highlight) return null;

  const isOpenHouse = highlight === 'open_house';

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          'w-full flex items-center gap-4 rounded-2xl border border-secondary shadow bg-primary px-4 py-4 hover:bg-secondary active:bg-secondary transition-colors text-left',
          containerStyle,
        )}
      >
        {isOpenHouse ? (
          <IconCalendarDuotone width={24} height={24} className="icon-verified-bg" />
        ) : (
          <IconVideoDuotone width={24} height={24} className="icon-verified-bg" />
        )}
        <div className="flex-1 space-y-1">
          <p className="text-body-2 text-primary">
            {isOpenHouse ? 'Open house · By appointment' : 'Video tour'}
          </p>
          {isOpenHouse && listing.openHouseStartAt ? (
            <p className="text-caption text-secondary">
              {formatOpenHouseDatetime(listing.openHouseStartAt)}
            </p>
          ) : null}
        </div>
        <IconChevronRight width={12} height={12} className="icon-tertiary" />
      </button>

      {/* Chat overlay – only used in the mweb left-column case (sidebar manages its own) */}
      {!onClick && isChatOpen ? (
        <ChatDetail
          variant="overlay"
          open={isChatOpen}
          onClose={handleClose}
          listing={listing}
          tourType={highlight}
        />
      ) : null}
    </>
  );
}
